import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";

export const fmtInr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
export const BLANK_LINE = { catalog_id: "", item_code: "", machine_model: "", uom: "PCS", quantity: 1, unit_price: 0, discount_amount: 0 };

export const lineMath = (it) => {
  const gross = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
  const disc = Math.min(Math.max(0, Number(it.discount_amount) || 0), gross);
  return { gross, disc, net: Math.max(0, gross - disc) };
};
export const itemsTotals = (items) => items.reduce((s, it) => {
  const m = lineMath(it);
  return { subtotal: s.subtotal + m.gross, discount: s.discount + m.disc, total: s.total + m.net };
}, { subtotal: 0, discount: 0, total: 0 });

const CodeHint = ({ it, catalog, testId }) => {
  const { user } = useAuth();
  const cat = it.catalog_id && catalog.find((c) => c.id === it.catalog_id);
  if (!cat || cat.item_code || (it.item_code || "").trim() || !can(user, "catalog", "view")) return null;
  return <div className="text-[10px] text-amber-600 mt-0.5 leading-tight" data-testid={testId}>Set item code in Machine Catalog</div>;
};

const L = ({ children }) => <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{children}</Label>;

function CatalogPick({ it, catalog, onPick, testId, showPrice }) {
  return (
    <Select value={it.catalog_id || ""} onValueChange={onPick}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={it.machine_model || "Pick a machine model"} /></SelectTrigger>
      <SelectContent className="max-h-72">
        {catalog.length === 0 && <SelectItem value="none" disabled>No catalog items</SelectItem>}
        {catalog.map((c) => (
          <SelectItem key={c.id} value={c.id}>{c.item_code ? `${c.item_code} · ` : ""}{c.model_name} — {c.manufacturer}{showPrice ? ` · ₹${Number(c.list_price || 0).toLocaleString("en-IN")}` : ""}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Row({ it, i, p, editable, rental, catalog, setItem, pick, rm, canRemove }) {
  const { gross, disc, net } = lineMath(it);
  const discNote = disc > 0 && <div className="text-[10px] text-amber-600 font-normal">− {fmtInr(disc)} off {fmtInr(gross)}</div>;
  const num = (k, extra = {}) => ({ type: "number", min: "0", value: it[k] ?? 0, disabled: !editable, onChange: (e) => setItem(i, { [k]: e.target.value }), ...extra });
  return (
    <div data-testid={`${p}line-item-${i}`}>
      <div className="hidden md:grid grid-cols-12 gap-2 p-2 items-center">
        <div className="col-span-2">
          <Input className="font-mono text-xs" placeholder="Code" value={it.item_code || ""} disabled={!editable} onChange={(e) => setItem(i, { item_code: e.target.value })} data-testid={`${p}li-code-${i}`} />
          {editable && <CodeHint it={it} catalog={catalog} testId={`${p}li-code-hint-${i}`} />}
        </div>
        <div className="col-span-3">{editable ? <CatalogPick it={it} catalog={catalog} onPick={(v) => pick(i, v)} testId={`${p}li-catalog-${i}`} showPrice={!rental} /> : <div className="text-sm text-slate-900 py-2">{it.machine_model}</div>}</div>
        <Input className="col-span-1 text-right" step="1" {...num("quantity")} data-testid={`${p}li-qty-${i}`} />
        <Input className="col-span-1 text-center" placeholder="PCS" value={it.uom || "PCS"} disabled={!editable} onChange={(e) => setItem(i, { uom: e.target.value })} data-testid={`${p}li-uom-${i}`} />
        <Input className="col-span-2 text-right" step="0.01" {...num("unit_price")} data-testid={`${p}li-price-${i}`} />
        <Input className="col-span-1 text-right" step="0.01" {...num("discount_amount")} data-testid={`${p}li-discount-${i}`} />
        <div className="col-span-2 text-right text-sm font-medium text-slate-800" data-testid={`${p}li-net-${i}`}>{fmtInr(net)}{rental && <span className="text-[10px] text-slate-500 font-normal"> /mo</span>}{discNote}</div>
        {editable && canRemove && <button type="button" onClick={() => rm(i)} className="col-span-12 justify-self-end text-xs text-red-500 hover:underline" data-testid={`${p}li-rm-${i}`}>Remove line</button>}
      </div>
      <div className="md:hidden p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Item {i + 1}</span>
          {editable && canRemove && <button type="button" onClick={() => rm(i)} className="text-xs text-red-500" data-testid={`${p}li-rm-m-${i}`}>Remove</button>}
        </div>
        {editable ? <CatalogPick it={it} catalog={catalog} onPick={(v) => pick(i, v)} testId={`${p}li-catalog-m-${i}`} /> : <div className="text-sm font-medium text-slate-900">{it.machine_model}</div>}
        <div className="grid grid-cols-2 gap-2">
          <div><L>Item code</L><Input className="font-mono text-xs" value={it.item_code || ""} disabled={!editable} onChange={(e) => setItem(i, { item_code: e.target.value })} data-testid={`${p}li-code-m-${i}`} />{editable && <CodeHint it={it} catalog={catalog} testId={`${p}li-code-hint-m-${i}`} />}</div>
          <div><L>UOM</L><Input placeholder="PCS" value={it.uom || "PCS"} disabled={!editable} onChange={(e) => setItem(i, { uom: e.target.value })} data-testid={`${p}li-uom-m-${i}`} /></div>
          <div><L>Qty</L><Input step="1" inputMode="numeric" {...num("quantity")} data-testid={`${p}li-qty-m-${i}`} /></div>
          <div><L>{rental ? "Monthly rent / unit ₹" : "Unit price ₹"}</L><Input step="0.01" inputMode="decimal" {...num("unit_price")} data-testid={`${p}li-price-m-${i}`} /></div>
        </div>
        <div><L>Discount ₹</L><Input step="0.01" inputMode="decimal" {...num("discount_amount")} data-testid={`${p}li-discount-m-${i}`} /></div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <span className="text-xs text-slate-500">{rental ? "Per month" : "Line total"}</span>
          <div className="text-right"><div className="text-sm font-semibold text-slate-900" data-testid={`${p}li-net-m-${i}`}>{fmtInr(net)}</div>{discNote}</div>
        </div>
      </div>
    </div>
  );
}

export function LineItemsEditor({ items, onChange, catalog, editable, rental, testPrefix = "" }) {
  const p = testPrefix;
  const setItem = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const pick = (i, catalog_id) => {
    const cat = catalog.find((c) => c.id === catalog_id);
    if (!cat) return;
    // Rental rent is typed manually; list price is only used for sale lines.
    setItem(i, { catalog_id, machine_model: cat.model_name, item_code: cat.item_code || "", ...(rental ? {} : { unit_price: Number(cat.list_price) || 0 }) });
  };
  const t = itemsTotals(items);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{rental ? "Line items · monthly rental (ex-GST)" : "Line items"}</div>
        {editable && <Button size="sm" variant="outline" onClick={() => onChange([...items, { ...BLANK_LINE }])} data-testid={`${p}add-line-item`}><Plus className="w-3 h-3 mr-1" /> Add line</Button>}
      </div>
      <div className="border border-slate-200 rounded-md divide-y">
        <div className="hidden md:grid grid-cols-12 gap-2 p-2 text-xs font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
          <div className="col-span-2">Item Code</div><div className="col-span-3">Machine / model</div>
          <div className="col-span-1 text-right">Qty</div><div className="col-span-1 text-center">UOM</div>
          <div className="col-span-2 text-right">{rental ? "Rent / unit / mo" : "Unit price"}</div>
          <div className="col-span-1 text-right">Disc. ₹</div><div className="col-span-2 text-right">{rental ? "Per month" : "Line"}</div>
        </div>
        {items.map((it, i) => <Row key={i} {...{ it, i, p, editable, rental, catalog, setItem, pick }} rm={(x) => onChange(items.filter((_, idx) => idx !== x))} canRemove={items.length > 1} />)}
      </div>
      <div className="mt-3 space-y-1 text-sm text-slate-600 md:max-w-xs md:ml-auto">
        <div className="flex justify-between"><span>Subtotal (gross)</span><span data-testid={`${p}subtotal-amount`}>{fmtInr(t.subtotal)}</span></div>
        {t.discount > 0 && <div className="flex justify-between"><span>Total discount</span><span data-testid={`${p}discount-total`} className="text-amber-700">{`− ${fmtInr(t.discount)}`}</span></div>}
        <div className="flex justify-between text-slate-900 font-semibold text-lg pt-1 border-t border-slate-200">
          <span>{rental ? "Monthly rental" : "Total (Ex-GST)"}</span>
          <span data-testid={`${p}total-amount`}>{fmtInr(t.total)}{rental && <span className="text-xs font-normal text-slate-500"> / month</span>}</span>
        </div>
      </div>
    </div>
  );
}
